import type { AppEvent } from "../calendar/types";
import type { Settings } from "../settings/types";
import { getNotificationPermission } from "./permission";
import { addReminderInboxItem, markReminderInboxItemRead } from "./reminderInbox";

// Removed unused type FiredKey

const FIRED_STORAGE_KEY = "notif_fired_v1";
const LAST_STORAGE_KEY = "notif_last_v1";

function dayKey(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function loadFired(todayKey: string): Set<string> {
  try {
    const raw = localStorage.getItem(FIRED_STORAGE_KEY);
    if (!raw) return new Set();
    const obj = JSON.parse(raw) as { day: string; keys: string[] };
    if (obj.day !== todayKey) return new Set(); // ����� ���� � �������
    return new Set(obj.keys || []);
  } catch {
    return new Set();
  }
}

function saveFired(set: Set<string>) {
  try {
    localStorage.setItem(
      FIRED_STORAGE_KEY,
      JSON.stringify({ day: firedDay, keys: Array.from(set).slice(-500) }) // �����
    );
  } catch {
    // ignore
  }
}

/** session cache to prevent repeat spam */
let firedDay = dayKey(new Date());
const fired = loadFired(firedDay);

function refreshFiredDay(now: number) {
  const nextDay = dayKey(new Date(now));
  if (nextDay !== firedDay) {
    firedDay = nextDay;
    fired.clear();
    saveFired(fired);
  }
}

function keyFor(uid: string, kind: string, minutes: number) {
  return `${uid}:${kind}:${minutes}`;
}

async function notify(id: string, title: string, body: string, meta?: { kind?: "deadline" | "lesson"; dueAt?: number }) {
  addReminderInboxItem({
    id,
    title,
    body,
    at: Date.now(),
    kind: meta?.kind,
    dueAt: meta?.dueAt,
  });

  try {
    localStorage.setItem(
      LAST_STORAGE_KEY,
      JSON.stringify({ at: Date.now(), id, kind: meta?.kind, dueAt: meta?.dueAt })
    );
  } catch {
    // ignore
  }

  const perm = getNotificationPermission();
  if (perm !== "granted") return;

  const options: NotificationOptions = {
    body,
    tag: id, // �����������
    icon: "/icons/icon-192.png",
    badge: "/icons/icon-192.png",
    silent: false,
  };

  // 1) ���� PWA / ���� SW � ���������� ����� ���� (�������)
  try {
    if ("serviceWorker" in navigator) {
      const reg = await navigator.serviceWorker.ready;
      await reg.showNotification(title, options);
      return;
    }
  } catch {
    // fallback ����
  }

  // 2) fallback: ������� Notification (�������� ���� �������� �������)
  try {
    const n = new Notification(title, options);
    n.onclick = () => {
      markReminderInboxItemRead(id);
      try {
        window.focus();
      } catch {
        // ignore
      }
    };
  } catch {
    // ignore
  }
}
type Lang = "ru" | "en" | "kk";

function langFromSettings(settings: Settings): Lang {
  const lang = settings.language || "ru";
  return lang === "en" || lang === "kk" || lang === "ru" ? lang : "ru";
}

const messages = {
  ru: {
    deadlineTitle: "����� �������",
    deadlineBody: (title: string, minutes: number) => `${title} - �������� ${minutes} ���`,
    firstLessonTitle: "������ ����",
    firstLessonBody: (title: string, minutes: number) => `${title} �������� ����� ${minutes} ���`,
    lessonSoonTitle: "����� ����",
    lessonSoonBody: (title: string, minutes: number) => `${title} �������� ����� ${minutes} ���`
  },
  en: {
    deadlineTitle: "Deadline soon",
    deadlineBody: (title: string, minutes: number) => `${title} - ${minutes} min left`,
    firstLessonTitle: "First lesson",
    firstLessonBody: (title: string, minutes: number) => `${title} starts in ${minutes} min`,
    lessonSoonTitle: "Lesson soon",
    lessonSoonBody: (title: string, minutes: number) => `${title} starts in ${minutes} min`
  },
  kk: {
    deadlineTitle: "������� �������",
    deadlineBody: (title: string, minutes: number) => `${title} - ${minutes} ��� �����`,
    firstLessonTitle: "������ ����?",
    firstLessonBody: (title: string, minutes: number) => `${title} ${minutes} ��� ���� ���������`,
    lessonSoonTitle: "����? �������",
    lessonSoonBody: (title: string, minutes: number) => `${title} ${minutes} ��� ���� ���������`
  }
} as const;

function minutesBefore(ms: number, minutes: number) {
  return ms - minutes * 60_000;
}

function inNextWindow(ts: number, now: number, windowMs: number) {
  return ts >= now && ts <= now + windowMs;
}

function isLesson(e: AppEvent) {
  return e.kind === "lesson";
}
function isDeadlineLike(e: AppEvent) {
  return e.kind === "deadline" || e.kind === "assignment";
}

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function sameDay(a: number, b: number) {
  const da = new Date(a);
  const db = new Date(b);
  return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
}

/**
 * Call this every N seconds/minutes from a hook.
 * windowMs: how far ahead we look for triggers (e.g. 65s or 2min).
 */
export function tickNotifications(events: AppEvent[], settings: Settings, windowMs = 65_000) {
  if (!settings.remindersEnabled) return;

  const now = Date.now();
  refreshFiredDay(now);
  const q = settings.quietHours;
  let inQuiet = false;
  let allowLessonsInQuiet = false;

  if (q) {
    const d = new Date(now);
    const h = d.getHours();
    const min = d.getMinutes();
    const crossesMidnight = q.from >= q.to;
    inQuiet = q.from < q.to ? (h >= q.from && h < q.to) : h >= q.from || h < q.to;

    if (inQuiet && settings.lessonRemindersEnabled && crossesMidnight && h < q.to) {
      const minutesToQuietEnd = q.to * 60 - (h * 60 + min);
      allowLessonsInQuiet = minutesToQuietEnd >= 0 && minutesToQuietEnd <= 120;
    }

    if (inQuiet && !allowLessonsInQuiet) return;
  }
  const lang = langFromSettings(settings);
  const msg = messages[lang];

  // ----- DEADLINES -----
  if (settings.deadlineRemindersEnabled) {
    if (inQuiet) {
      // respect quiet hours for deadlines
    } else {
      const mins = (settings.deadlineReminderMinutes || []).filter((x) => Number.isFinite(x) && x > 0);

      for (const e of events.filter(isDeadlineLike)) {
        const dueMs = new Date(e.due || e.start).getTime();
        if (!Number.isFinite(dueMs) || dueMs < now - 60_000) continue; // already passed

        for (const m of mins) {
          const fireAt = minutesBefore(dueMs, m);
          if (!inNextWindow(fireAt, now, windowMs)) continue;

          const k = keyFor(e.uid, "deadline", m);
          if (fired.has(k)) continue;

          fired.add(k);
          saveFired(fired);
          notify(k, msg.deadlineTitle, msg.deadlineBody(e.title, m), { kind: "deadline", dueAt: dueMs });
        }
      }
    }
  }

  // ----- LESSONS -----
  if (settings.lessonRemindersEnabled) {
    const todayStart = startOfDay(new Date(now));

    const lessonsToday = events
      .filter(isLesson)
      .filter((e) => sameDay(new Date(e.start).getTime(), todayStart))
      .slice()
      .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

    if (lessonsToday.length > 0) {
      const first = lessonsToday[0];

      const firstM = Math.max(1, settings.firstLessonReminderMinutes || 15);
      const firstStart = new Date(first.start).getTime();
      const firstFireAt = minutesBefore(firstStart, firstM);

      if (inNextWindow(firstFireAt, now, windowMs)) {
        const k = keyFor(first.uid, "lesson_first", firstM);
        if (!fired.has(k)) {
          fired.add(k);
          saveFired(fired);
          notify(k, msg.firstLessonTitle, msg.firstLessonBody(first.title, firstM), { kind: "lesson" });
        }
      }

      const lessonMins = (settings.lessonReminderMinutes || [])
        .filter((x) => Number.isFinite(x) && x > 0)
        .map((x) => Math.round(x));

      for (const e of lessonsToday.slice(1)) {
        const st = new Date(e.start).getTime();
        if (!Number.isFinite(st)) continue;

        for (const m of lessonMins) {
          const fireAt = minutesBefore(st, m);
          if (!inNextWindow(fireAt, now, windowMs)) continue;

          const k = keyFor(e.uid, "lesson", m);
          if (fired.has(k)) continue;

          fired.add(k);
          saveFired(fired);
          notify(k, msg.lessonSoonTitle, msg.lessonSoonBody(e.title, m), { kind: "lesson" });
        }
      }
    }
  }
}
export function computeCandidateFireTimes(events: AppEvent[], settings: Settings, horizonMs = 12 * 60 * 60_000) {
  if (!settings.remindersEnabled) return [];
  const now = Date.now();
  const horizon = now + horizonMs;

  const times: number[] = [];

  function pushIf(ts: number) {
    if (ts >= now && ts <= horizon && Number.isFinite(ts)) times.push(ts);
  }

  if (settings.deadlineRemindersEnabled) {
    const mins = (settings.deadlineReminderMinutes || []).filter((x) => Number.isFinite(x) && x > 0);
    for (const e of events.filter(isDeadlineLike)) {
      const dueMs = new Date(e.due || e.start).getTime();
      if (!Number.isFinite(dueMs) || dueMs < now - 60_000) continue;
      for (const m of mins) pushIf(minutesBefore(dueMs, m));
    }
  }

  if (settings.lessonRemindersEnabled) {
    const todayStart = startOfDay(new Date(now));
    const lessonsToday = events
      .filter(isLesson)
      .filter((e) => sameDay(new Date(e.start).getTime(), todayStart))
      .slice()
      .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

    if (lessonsToday.length > 0) {
      const first = lessonsToday[0];
      const firstM = Math.max(1, settings.firstLessonReminderMinutes || 15);
      pushIf(minutesBefore(new Date(first.start).getTime(), firstM));

      const lessonMins = (settings.lessonReminderMinutes || [])
        .filter((x) => Number.isFinite(x) && x > 0)
        .map((x) => Math.round(x));

      for (const e of lessonsToday.slice(1)) {
        const st = new Date(e.start).getTime();
        for (const m of lessonMins) pushIf(minutesBefore(st, m));
      }
    }
  }

  return Array.from(new Set(times)).sort((a, b) => a - b);
}